/**
 * MT5 Deep Link Helper
 * =====================
 * Opens MetaTrader 5 app with pre-filled trade parameters.
 *
 * Strategy (best-effort, layered fallbacks):
 * 1. Copy trade detail to clipboard (GUARANTEED to work everywhere)
 * 2. Attempt MT5 mobile deep link (metatrader5://) — works on iOS/Android with MT5 installed
 * 3. Attempt MT5 web trader URL (https://trade.mql5.com/trade?...) — works on desktop browser
 * 4. Show in-app instructions modal with copy-paste guide
 *
 * Returns: { opened: bool, method: string, clipboardCopied: bool, instructions: string }
 */

// Convert pair to MT5 symbol format
// "XAU/USD" -> "XAUUSD" (MT5 uses no separator)
// "EUR/USD" -> "EURUSD"
const toMt5Symbol = (pair) => {
  if (!pair) return 'XAUUSD';
  return String(pair).toUpperCase().replace(/[\/\-_.]/g, '');
};

const formatPrice = (p) => {
  if (p == null || p === '') return '';
  return Number(p).toFixed(2);
};

/**
 * Build a text block summarizing the trade for clipboard.
 * Broker-agnostic format — user can paste anywhere.
 */
export const buildTradeClipboard = (signal, lotSize = null) => {
  const symbol = toMt5Symbol(signal.pair);
  const side = signal.direction || 'BUY';
  const entry = signal.entry_zone?.from || signal.entry_price;
  const sl = signal.stop_loss;
  const tp = Array.isArray(signal.take_profit) ? signal.take_profit[0] : signal.take_profit;

  const lines = [
    `${symbol} ${side}`,
    entry ? `Entry: ${formatPrice(entry)}` : '',
    sl ? `SL: ${formatPrice(sl)}` : '',
    tp ? `TP: ${formatPrice(tp)}` : '',
    lotSize != null ? `Lot: ${Number(lotSize).toFixed(2)}` : '',
    signal.timeframe ? `TF: ${signal.timeframe}` : '',
    signal.confidence != null ? `Confidence: ${signal.calibrated_confidence || signal.confidence}%` : '',
  ].filter(Boolean);

  return lines.join('\n');
};

/**
 * Try to build metatrader5:// deep link (mobile).
 * Format:
 *   metatrader5://trade?symbol=XAUUSD&type=0&volume=0.01&sl=...&tp=...&price=...
 * type: 0=buy, 1=sell
 * Note: MT5 mobile app URL scheme is not officially documented across all versions.
 * This opens the symbol in the app; user confirms trade manually.
 */
export const buildMt5MobileLink = (signal, lotSize = 0.01) => {
  const symbol = toMt5Symbol(signal.pair);
  const side = (signal.direction || 'BUY').toUpperCase();
  const type = side === 'BUY' ? 0 : 1;
  const entry = signal.entry_zone?.from || signal.entry_price;
  const sl = signal.stop_loss;
  const tp = Array.isArray(signal.take_profit) ? signal.take_profit[0] : signal.take_profit;

  const params = new URLSearchParams({
    symbol,
    type: String(type),
    volume: String(Number(lotSize).toFixed(2)),
  });
  if (entry) params.set('price', formatPrice(entry));
  if (sl) params.set('sl', formatPrice(sl));
  if (tp) params.set('tp', formatPrice(tp));

  return `metatrader5://trade?${params.toString()}`;
};

/**
 * Desktop/web MT5 web trader (fallback).
 */
export const buildMt5WebLink = (signal) => {
  const symbol = toMt5Symbol(signal.pair);
  return `https://www.metatrader5.com/en/mobile-trading?symbol=${symbol}`;
};

/**
 * Attempt copy to clipboard (modern + fallback).
 * Returns true on success.
 */
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

/**
 * Detect if user is on mobile (ios/android).
 */
const isMobile = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

/**
 * Main executor — called from "Execute in MT5" button.
 */
export const executeInMt5 = async (signal, lotSize = null) => {
  const clipboardText = buildTradeClipboard(signal, lotSize);
  const clipboardCopied = await copyToClipboard(clipboardText);

  const mobile = isMobile();
  let opened = false;
  let method = 'clipboard_only';

  if (mobile) {
    // Try MT5 mobile deep link first
    try {
      const deepLink = buildMt5MobileLink(signal, lotSize || 0.01);
      // iOS/Android both support this — assign to window.location triggers app
      window.location.href = deepLink;
      opened = true;
      method = 'mt5_mobile_deeplink';
    } catch {
      opened = false;
    }
  } else {
    // Desktop — opening protocol handler via window.open
    try {
      const deepLink = buildMt5MobileLink(signal, lotSize || 0.01);
      const w = window.open(deepLink, '_blank');
      if (w) {
        opened = true;
        method = 'mt5_desktop_protocol';
      }
    } catch {
      opened = false;
    }
    if (!opened) {
      // Fallback: open web trader info page
      try {
        window.open(buildMt5WebLink(signal), '_blank');
        opened = true;
        method = 'mt5_web_info';
      } catch {
        opened = false;
      }
    }
  }

  return {
    opened,
    method,
    clipboardCopied,
    clipboardText,
    instructions: mobile
      ? 'Trade details copied. If MT5 app did not open, paste into "New Order" dialog.'
      : 'Trade details copied. Open MT5 on your device and paste into New Order.',
  };
};
